import React, { useRef, useEffect, useCallback } from "react";
import { fabric } from "fabric";

import { useAppContext } from "../../../../hooks/useAppContext";
import { Box, Button } from "@mui/material";
import useEditVideoFile from "../../../../backend/useEditVideoFile";
import LinearProgressWithLabel from "./LinearProgressWithLabel";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../../hooks/AIKernel"; // Adjust the relative path as needed

const VideoCanvasOverlay: React.FC = () => {
  const { videoRef, isProcessing, logoEditorToggle } = useAppContext();
  const { overlayImageOnVideo, progress } = useEditVideoFile();
  
  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Cast as <any> to bypass the tricky Fabric.js namespace error
  const fabricCanvas = useRef<any>(null);

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  // Handle Delete key to remove selected object
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      if (fabricCanvas.current) {
        const activeObject = fabricCanvas.current.getActiveObject();
        if (activeObject) {
          fabricCanvas.current.remove(activeObject);
          // TELEMETRY: User removed a logo (tinkering/adjusting)
          if (reportBehavior) reportBehavior("logo_adjusted", 1, "behavior");
        }
      }
    }
  }, [reportBehavior]);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const addImageToCanvas = useCallback((data: string) => {
    if (typeof data === "string" && fabricCanvas.current) {
      // Explicitly type 'img' as 'any' to satisfy TS strict mode
      fabric.Image.fromURL(data, (img: any) => {
        const canvas = fabricCanvas.current;

        if (img.width && img.height && canvas?.width && canvas.height) {
          const scaleX = canvas.width / img.width / 2;
          const scaleY = canvas.height / img.height / 2;
          const scale = Math.min(scaleX, scaleY);

          if (scale < 1) {
            img.scale(scale);
          }

          canvas.add(img);
          canvas.renderAll();

          // TELEMETRY: User successfully placed a logo on the canvas
          if (reportBehavior) reportBehavior("branding_focus", 3, "interest");
        }
      });
    }
  }, [reportBehavior]);

  const handleClick = useCallback(() => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const file = target.files[0];
        const reader = new FileReader();

        reader.onload = function (fileEvent: ProgressEvent<FileReader>) {
          const data = fileEvent.target?.result as string;
          addImageToCanvas(data);
        };

        reader.readAsDataURL(file);
      }
    };
    fileInput.click();
  }, [addImageToCanvas]);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (event.dataTransfer && event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0];
        
        // TELEMETRY: Friction check - Did they drop a non-image file?
        if (!file.type.startsWith("image/")) {
          if (reportBehavior) reportBehavior("invalid_logo_drop", 2, "behavior");
          return;
        }

        const reader = new FileReader();

        reader.onload = function (fileEvent: ProgressEvent<FileReader>) {
          const data = fileEvent.target?.result as string;
          addImageToCanvas(data);
        };

        reader.readAsDataURL(file);
      }
    },
    [addImageToCanvas, reportBehavior]
  );

  const handleExportCanvas = useCallback(async () => {
    if (!fabricCanvas.current) {
      return;
    }

    fabricCanvas.current.discardActiveObject().renderAll();
    fabricCanvas.current.getElement().style.pointerEvents = "none";

    const multiplier =
      fabricCanvas.current.width && videoRef?.videoWidth
        ? videoRef?.videoWidth / fabricCanvas.current.width
        : 1;

    const imageUrl = fabricCanvas.current.toDataURL({
      format: "png",
      quality: 1,
      multiplier,
    });

    const resultBlobUrl = await overlayImageOnVideo(imageUrl);

    if (!resultBlobUrl || !videoRef) {
      // TELEMETRY: Rendering failed
      if (reportBehavior) reportBehavior("video_render_error", 2, "behavior");
      return;
    }

    // TELEMETRY: Highly successful branding application
    if (reportBehavior) reportBehavior("branding_applied_success", 10, "interest");

    logoEditorToggle();

    videoRef.src = resultBlobUrl;
  }, [logoEditorToggle, overlayImageOnVideo, videoRef, reportBehavior]);

  useEffect(() => {
    // Set up Fabric.js canvas
    if (canvasRef.current && !fabricCanvas.current) {
      fabricCanvas.current = new fabric.Canvas(canvasRef.current, {
        preserveObjectStacking: true,
      });
    }

    // Update canvas size to match video size
    const updateCanvasSize = () => {
      if (videoRef && fabricCanvas.current) {
        fabricCanvas.current.setDimensions({
          width: videoRef.offsetWidth,
          height: videoRef.offsetHeight,
        });
      }
    };

    window.addEventListener("resize", updateCanvasSize);
    updateCanvasSize();

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, videoRef]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: isProcessing ? "none" : "auto", // Prevent interaction while processing
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", top: 0, left: 0 }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10px", // Moved up slightly from -16px for better visibility
          display: "flex",
          width: "100%",
          justifyContent: "center", // Center the controls over the video
          gap: "12px",
          padding: "8px",
          // ADAPTIVE UI: Glassmorphism background based on theme
          background: isDarkMode ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)",
          backdropFilter: "blur(8px)",
          borderRadius: "8px",
          transition: "background 0.5s ease",
          "& > button": {
            textTransform: "none",
            padding: "6px 16px",
            minHeight: 0,
            lineHeight: "20px",
            fontWeight: "bold",
          },
        }}
      >
        <Button
          disabled={isProcessing}
          size="small"
          variant="contained"
          color={isDarkMode ? "secondary" : "primary"}
          onClick={handleClick}
        >
          Add Logo
        </Button>
        
        <Button
          disabled={isProcessing}
          size="small"
          variant="contained"
          color={highlightCTA ? "success" : "primary"}
          onClick={handleExportCanvas}
          sx={{
            // ADAPTIVE UI: Pulse effect if the user is frustrated/confused to guide them
            animation: highlightCTA && !isProcessing ? "pulse 2s infinite" : "none",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0.7)" },
              "70%": { boxShadow: "0 0 0 10px rgba(0, 229, 255, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0)" }
            }
          }}
        >
          Apply Logo
        </Button>
        {isProcessing && (
          <Box sx={{ width: "150px", alignSelf: "center", ml: 2 }}>
            <LinearProgressWithLabel value={progress} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoCanvasOverlay;